import type { Schemas } from '@npc/api-client'

import { api } from './api'
import { ApiFailure, call } from './errors'
import { t } from '../copy'

export type TaskUserState = 'claimable' | 'in_progress' | 'live' | 'needs_action' | 'cancelled'

export type DevelopmentTask = Schemas['DevelopmentTask'] & {
  stale?: boolean
  user_state?: TaskUserState
  next_action?: TaskUserState
  release_id?: string | null
  candidate_sha?: string | null
  current_main_sha?: string | null
  lineage_relation?: string | null
  deployment_sha?: string | null
  production_verified_at?: string | null
  needs_rebase?: boolean
}

export type DevelopmentTaskListQuery = {
  assignee?: string
  user_state?: TaskUserState
  updated_after?: string
}
export type DevelopmentTaskEvent = Schemas['DevelopmentTaskEvent']
export type CreateDevelopmentTaskInput = Schemas['DevelopmentTaskCreate']
export type DevelopmentTaskClaim = Schemas['DevelopmentTaskClaimEnvelope']
export type DevelopmentTaskOptions = Schemas['DevelopmentTaskOptions']
export type DevelopmentTaskRepoOption = Schemas['DevelopmentTaskRepoOption']
export type DevelopmentTaskPrepareInput = Schemas['DevelopmentTaskPrepare']
export type DevelopmentTaskExecutorIdentity = NonNullable<
  Schemas['DevelopmentTaskClaimRequest']['executor_identity']
>
export type DevelopmentTaskPreviewReceipt = Schemas['DevelopmentTaskPreviewReceipt']
export type DevelopmentTaskPreviewState = Schemas['DevelopmentTaskPreviewState']
export type DevelopmentTaskReleaseReceipt = Schemas['DevelopmentTaskReleaseReceipt']
export type DevelopmentTaskReleaseState = Schemas['DevelopmentTaskReleaseState']
export type DevelopmentTaskMagicLink = Schemas['DevelopmentTaskMagicLink']
export type DevelopmentTaskMagicLinkResolved = Schemas['DevelopmentTaskMagicLinkResolved']
export type DevelopmentTaskMaintainerResolved = Schemas['DevelopmentTaskMaintainerResolved']
export type DevelopmentTaskMaintainerOnboardingBundle = Schemas['DevelopmentTaskMaintainerOnboardingBundle']

export function isDevelopmentTaskExecutorId(value: string): boolean {
  if (!value || new TextEncoder().encode(value).length > 128) return false
  return [...value].every((character) => {
    const codepoint = character.codePointAt(0) ?? 0
    return codepoint > 0x20 && codepoint !== 0x7f && character !== '/' && character !== '\\'
  })
}

export async function getDevelopmentTaskOptions(
  projectId: string,
): Promise<DevelopmentTaskOptions> {
  return call('/projects/{projectId}/development-task-options', () =>
    api.GET('/projects/{projectId}/development-task-options', {
      params: { path: { projectId } },
    }),
  )
}

export async function listProjectDevelopmentTasks(
  projectId: string,
  query: DevelopmentTaskListQuery = {},
): Promise<DevelopmentTask[]> {
  const response = await call('/projects/{projectId}/development-tasks', () =>
    api.GET('/projects/{projectId}/development-tasks', {
      params: {
        path: { projectId },
        query: {
          assignee: query.assignee,
          user_state: query.user_state,
          updated_after: query.updated_after,
        },
      },
    }),
  )
  return response.items
}

export async function createDevelopmentTask(
  projectId: string,
  input: CreateDevelopmentTaskInput,
): Promise<DevelopmentTask> {
  const response = await call('/projects/{projectId}/development-tasks', () =>
    api.POST('/projects/{projectId}/development-tasks', {
      params: { path: { projectId } },
      body: input,
    }),
  )
  return response.task
}

export async function getDevelopmentTask(taskId: string): Promise<DevelopmentTask> {
  const response = await call('/development-tasks/{taskId}', () =>
    api.GET('/development-tasks/{taskId}', { params: { path: { taskId } } }),
  )
  return response.task
}

export async function prepareDevelopmentTask(
  taskId: string,
  input: DevelopmentTaskPrepareInput = {},
): Promise<DevelopmentTask> {
  const response = await call('/development-tasks/{taskId}/prepare', () =>
    api.POST('/development-tasks/{taskId}/prepare', {
      params: { path: { taskId } },
      body: input,
    }),
  )
  return response.task
}

/**
 * 让当前 attempt 交出的那一版先跑一遍预览。
 *
 * `sha` 只能是 attempt 已经交出的 `result_sha`——服务端会照着当前 attempt 逐字核对，
 * 对不上直接拒。前端不自己拼这一版是哪一个，也不允许用户手输。
 */
export async function requestDevelopmentTaskPreview(
  projectId: string,
  taskId: string,
  sha: string,
): Promise<DevelopmentTaskPreviewReceipt> {
  return call('/projects/{projectId}/development-tasks/{taskId}/preview-request', () =>
    api.POST('/projects/{projectId}/development-tasks/{taskId}/preview-request', {
      params: { path: { projectId, taskId } },
      body: { sha },
    }),
  )
}

export async function getDevelopmentTaskPreviewReceipt(
  projectId: string,
  taskId: string,
): Promise<DevelopmentTaskPreviewReceipt> {
  return call('/projects/{projectId}/development-tasks/{taskId}/preview-request', () =>
    api.GET('/projects/{projectId}/development-tasks/{taskId}/preview-request', {
      params: { path: { projectId, taskId } },
    }),
  )
}

export async function getDevelopmentTaskReleaseReceipt(
  projectId: string,
  taskId: string,
): Promise<DevelopmentTaskReleaseReceipt> {
  return call('/projects/{projectId}/development-tasks/{taskId}/release-request', () =>
    api.GET('/projects/{projectId}/development-tasks/{taskId}/release-request', {
      params: { path: { projectId, taskId } },
    }),
  )
}

export async function getDevelopmentTaskEvents(taskId: string): Promise<DevelopmentTaskEvent[]> {
  const response = await call('/development-tasks/{taskId}/events', () =>
    api.GET('/development-tasks/{taskId}/events', { params: { path: { taskId } } }),
  )
  return response.events
}

export async function cancelDevelopmentTask(taskId: string): Promise<DevelopmentTask> {
  const response = await call('/development-tasks/{taskId}/cancel', () =>
    api.POST('/development-tasks/{taskId}/cancel', { params: { path: { taskId } } }),
  )
  return response.task
}

export async function claimDevelopmentTask(
  taskId: string,
  executorId: string,
  executorIdentity?: DevelopmentTaskExecutorIdentity,
): Promise<DevelopmentTaskClaim> {
  return call('/development-tasks/{taskId}/claim', () =>
    api.POST('/development-tasks/{taskId}/claim', {
      params: { path: { taskId } },
      body: {
        executor_id: executorId,
        ...(executorIdentity ? { executor_identity: executorIdentity } : {}),
      },
    }),
  )
}

export async function createDevelopmentTaskMagicLink(taskId: string): Promise<DevelopmentTaskMagicLink> {
  return call('/development-tasks/{taskId}/magic-link', () =>
    api.POST('/development-tasks/{taskId}/magic-link', {
      params: { path: { taskId } },
    }),
  )
}

async function publicMagicTaskRequest<T>(path: 'resolve' | 'claim', token: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api/v1/public/development-task-links/${path}`, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    throw new Error(response.status === 404 ? t('development_task.magic.invalid') : t('development_task.magic.request_error'))
  }
  return response.json() as Promise<T>
}

export function resolveDevelopmentTaskMagicLink(token: string): Promise<DevelopmentTaskMagicLinkResolved> {
  return publicMagicTaskRequest('resolve', token)
}

export function claimDevelopmentTaskMagicLink(token: string, executorId: string): Promise<DevelopmentTaskClaim> {
  return publicMagicTaskRequest('claim', token, { executor_id: executorId })
}

/**
 * 身份有效期档位。**从生成的契约里取，不在这儿再写一遍。**
 *
 * 手写一份联合类型的代价不是多几个字：任务页与设置页于是各有一套周期规则，
 * 而同一个远程 AI 身份会因为「从哪个页面签的」而有不同的可选档位——
 * 那是用户看不见、也解释不通的差别。契约加一档，这里自动跟上。
 */
export type MaintainerOnboardingDuration =
  Schemas['DevelopmentTaskMaintainerOnboardingRequest']['duration']

export async function createDevelopmentTaskMaintainerOnboarding(
  taskId: string,
  label: string,
  duration: MaintainerOnboardingDuration,
  previousCredentialId?: string,
): Promise<DevelopmentTaskMaintainerOnboardingBundle> {
  return call('/development-tasks/{taskId}/maintainer-onboarding', () => api.POST('/development-tasks/{taskId}/maintainer-onboarding', {
    params: { path: { taskId } },
    body: { label: label.trim(), duration, ...(previousCredentialId ? { previous_credential_id: previousCredentialId } : {}) },
  }))
}

export async function revokeDevelopmentTaskMaintainerCredential(credentialId: string): Promise<void> {
  await call('/credentials/{credentialId}', () => api.DELETE('/credentials/{credentialId}', {
    params: { path: { credentialId } },
  }))
}

export async function resolveDevelopmentTaskMaintainerTask(
  taskId: string,
  token: string,
): Promise<DevelopmentTaskMaintainerResolved> {
  return bridgeTaskRequest('resolve', taskId, token)
}

export async function claimDevelopmentTaskMaintainerTask(
  taskId: string,
  token: string,
  executorId: string,
): Promise<DevelopmentTaskClaim> {
  return bridgeTaskRequest('claim', taskId, token, { executor_id: executorId })
}

async function bridgeTaskRequest<T>(path: 'resolve' | 'claim', taskId: string, token: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api/v1/bridge/development-tasks/${encodeURIComponent(taskId)}/${path}`, {
    method: 'POST', credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
    headers: { Authorization: `Bearer ${token}`, ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    let code: string | undefined
    try {
      const payload = await response.json() as { code?: unknown }
      if (typeof payload.code === 'string') code = payload.code
    } catch {
      // A non-JSON response is still a safe generic browser error.
    }
    throw new ApiFailure('reported', maintainerBridgeErrorMessage(code), { status: response.status, code })
  }
  return response.json() as Promise<T>
}

export function maintainerBridgeErrorMessage(code?: string): string {
  switch (code) {
    case 'development_task.maintainer.expired': return t('development_task.maintainer.error.expired')
    case 'development_task.maintainer.claimed': return t('development_task.maintainer.error.claimed')
    case 'development_task.maintainer.key_mismatch': return t('development_task.maintainer.error.key_mismatch')
    default: return t('development_task.magic.request_error')
  }
}

export function isExecutingDevelopmentTask(state: string): boolean {
  return state === 'running' || state === 'gating' || state === 'reviewing' || state === 'ci_running'
}

export function developmentTaskNeedsDecision(state: string, stale = false): boolean {
  if (state === 'failed' || state === 'ready' || state === 'cancelled' || state === 'v2_handoff') return false
  if (stale) return true
  return !isExecutingDevelopmentTask(state)
}

export function visibleTaskElapsedMs(task: {
  elapsed_ms?: number | null
  stale?: boolean
  state?: string
}): number | null {
  if (task.stale) return null
  if (task.state != null && !isExecutingDevelopmentTask(task.state)) return null
  if (task.elapsed_ms == null) return null
  return task.elapsed_ms
}

export type DevelopmentTaskComments = Schemas['DevelopmentTaskComments']

/** 一条任务上的评论、它自己的记忆与续修轮数（修复任务闭环 §C §D）。 */
export async function listDevelopmentTaskComments(taskId: string): Promise<DevelopmentTaskComments> {
  return call('/development-tasks/{taskId}/comments', () =>
    api.GET('/development-tasks/{taskId}/comments', { params: { path: { taskId } } }),
  )
}

/** 「没修好，还是 X」：一条人写的评论 = 一次续修请求。 */
export async function addDevelopmentTaskComment(taskId: string, body: string): Promise<DevelopmentTaskComments> {
  return call('/development-tasks/{taskId}/comments', () =>
    api.POST('/development-tasks/{taskId}/comments', {
      params: { path: { taskId } },
      body: { body },
    }),
  )
}
